import React, { useState } from 'react';
import { Plus, PencilSimple, Power } from 'phosphor-react';
import { useApi, useFetch } from '_api';
import { useAlertActions } from '_actions';
import { formatVND } from '_helpers';
import { PageHeader, Bezel, Table, Button, IconButton, Modal, Input, Select, Tag, MotionReveal, StatCard } from '_ui';

const TIER_LABEL = { chinh_thuc: 'Chính thức', thu_viec: 'Thử việc' };
const TIER_OPTIONS = [{ value: 'chinh_thuc', label: 'Chính thức' }, { value: 'thu_viec', label: 'Thử việc' }];
const blank = { name: '', price: '', commissionRate: '', tier: 'chinh_thuc' };

export function DichVu() {
    const { data, loading, refetch } = useFetch('/api/services?all=true');
    const api = useApi();
    const alert = useAlertActions();
    const [editing, setEditing] = useState(null); // service object, or {} for new, or null when closed
    const [showInactive, setShowInactive] = useState(false);
    const [tierFilter, setTierFilter] = useState('all'); // 'all' | 'chinh_thuc' | 'thu_viec'

    const services = data || [];
    const active = services.filter(s => s.active);
    const rows = services.filter(s =>
        (showInactive || s.active) && (tierFilter === 'all' || (s.tier || 'chinh_thuc') === tierFilter));

    async function toggleActive(svc) {
        const res = await api.put(`/api/services/${svc._id}`, {
            name: svc.name, price: svc.price, commissionRate: svc.commissionRate, tier: svc.tier, active: !svc.active,
        });
        if (res.ok) { alert.success(svc.active ? 'Đã ngừng dịch vụ' : 'Đã bật lại dịch vụ'); refetch(); }
        else alert.error('Không thể cập nhật', res.error);
    }

    const columns = [
        { key: 'name', header: 'Dịch vụ', render: s => <span className={s.active ? 'font-medium text-espresso' : 'text-espresso-soft/60 line-through'}>{s.name}</span> },
        { key: 'price', header: 'Giá', align: 'right', render: s => <span className="tnum">{formatVND(s.price)}</span> },
        { key: 'rate', header: '% hoa hồng', align: 'right', render: s => <span className="tnum text-sage-deep">{s.commissionRate}%</span> },
        { key: 'tier', header: 'Loại', align: 'center', render: s => <Tag tone={(s.tier || 'chinh_thuc') === 'thu_viec' ? 'brass' : 'neutral'}>{TIER_LABEL[s.tier] || 'Chính thức'}</Tag> },
        { key: 'status', header: 'Trạng thái', align: 'center', render: s => <Tag tone={s.active ? 'sage' : 'neutral'}>{s.active ? 'Đang bán' : 'Ngừng'}</Tag> },
        { key: 'actions', header: '', align: 'right', render: s => (
            <div className="flex justify-end gap-1">
                <IconButton icon={PencilSimple} label="Sửa" variant="ghost" onClick={() => setEditing(s)} />
                <IconButton icon={Power} label={s.active ? 'Ngừng' : 'Bật lại'} variant="ghost"
                    className={s.active ? 'hover:text-danger' : 'hover:text-sage-deep'} onClick={() => toggleActive(s)} />
            </div>
        ) },
    ];

    return (
        <div className="space-y-10">
            <PageHeader
                eyebrow="Quản lý"
                title="Dịch vụ"
                description="Giá và phần trăm hoa hồng áp dụng cho mọi nhân viên."
                actions={
                    <div className="flex flex-wrap items-center gap-3">
                        {['all', 'chinh_thuc', 'thu_viec'].map(t => (
                            <Button key={t} variant={tierFilter === t ? 'primary' : 'ghost'} size="sm" onClick={() => setTierFilter(t)}>
                                {t === 'all' ? 'Tất cả' : TIER_LABEL[t]}
                            </Button>
                        ))}
                        <Button variant="ghost" size="sm" onClick={() => setShowInactive(v => !v)}>
                            {showInactive ? 'Ẩn dịch vụ đã ngừng' : 'Hiện dịch vụ đã ngừng'}
                        </Button>
                        <Button icon={Plus} onClick={() => setEditing(blank)}>Thêm dịch vụ</Button>
                    </div>
                }
            />

            <div className="grid gap-5 sm:grid-cols-2">
                <MotionReveal><StatCard label="Đang bán" value={active.length} /></MotionReveal>
                <MotionReveal delay={0.05}><StatCard label="Tổng dịch vụ" value={services.length} /></MotionReveal>
            </div>

            <Bezel className="overflow-hidden">
                <Table columns={columns} rows={rows} loading={loading} empty={showInactive ? 'Chưa có dịch vụ nào. Bấm “Thêm dịch vụ”.' : 'Không có dịch vụ đang bán.'} />
            </Bezel>

            {editing && <ServiceModal service={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />}
        </div>
    );
}

function ServiceModal({ service, onClose, onSaved }) {
    const isEdit = !!service._id;
    const api = useApi();
    const alert = useAlertActions();
    const [form, setForm] = useState({
        name: service.name || '',
        price: service.price ?? '',
        commissionRate: service.commissionRate ?? '',
        tier: service.tier || 'chinh_thuc',
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

    function validate() {
        const e = {};
        if (!form.name.trim()) e.name = 'Nhập tên dịch vụ';
        if (form.price === '' || Number(form.price) < 0) e.price = 'Giá không hợp lệ';
        const r = Number(form.commissionRate);
        if (form.commissionRate === '' || r < 0 || r > 100) e.commissionRate = '% từ 0 đến 100';
        setErrors(e);
        return Object.keys(e).length === 0;
    }

    async function save() {
        if (!validate()) return;
        setSaving(true);
        const body = { name: form.name.trim(), price: Number(form.price), commissionRate: Number(form.commissionRate), tier: form.tier };
        const res = isEdit
            ? await api.put(`/api/services/${service._id}`, { ...body, active: service.active })
            : await api.post('/api/services', body);
        setSaving(false);
        if (res.ok) { alert.success(isEdit ? 'Đã lưu dịch vụ' : 'Đã thêm dịch vụ'); onSaved(); }
        else alert.error('Không thể lưu', res.error);
    }

    return (
        <Modal open onClose={onClose} eyebrow="Dịch vụ" title={isEdit ? 'Sửa dịch vụ' : 'Thêm dịch vụ'}
            footer={<><Button variant="ghost" onClick={onClose}>Huỷ</Button><Button onClick={save} loading={saving}>Lưu</Button></>}>
            <div className="space-y-5">
                <Input label="Tên dịch vụ" value={form.name} onChange={e => set('name', e.target.value)} error={errors.name} placeholder="VD: Cắt tóc" />
                <Select label="Loại nhân viên" value={form.tier} onChange={v => set('tier', v)} options={TIER_OPTIONS} />
                <div className="grid grid-cols-2 gap-4">
                    <Input label="Giá (VND)" type="number" min="0" value={form.price} onChange={e => set('price', e.target.value)} error={errors.price} placeholder="100000" />
                    <Input label="% hoa hồng" type="number" min="0" max="100" value={form.commissionRate} onChange={e => set('commissionRate', e.target.value)} error={errors.commissionRate} placeholder="20" />
                </div>
            </div>
        </Modal>
    );
}
